import React from "react";
import MuiButton, { ButtonProps as MuiButtonProps } from "@mui/material/Button";
import { Paper, PaperProps, Skeleton, SkeletonProps, useTheme } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import withStyles from "@mui/styles/withStyles";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import MuiGrid, { GridProps as MuiGridProps } from "@mui/material/Grid";

export const SPACING = 3;
export const BORDER_RADIUS = 6;

const buttonAtomStyles = createStyles(() => {
    return {
        root: {
            borderRadius: BORDER_RADIUS,
        },
    };
});

const ButtonAtomWithoutStyles: React.FunctionComponent<MuiButtonProps> = (props) => {
    return <MuiButton color="primary" variant="contained" disableRipple disableElevation {...props} />;
};
const Button = withStyles(buttonAtomStyles)(ButtonAtomWithoutStyles);

const Card = (props: PaperProps) => {
    return <Paper sx={{ borderRadius: `${BORDER_RADIUS}px` }} {...props} />;
};

const Grid: React.FunctionComponent<MuiGridProps> = (props) => {
    if (props.container) {
        return <MuiGrid spacing={SPACING} {...props} />;
    }
    return <MuiGrid {...props} />;
};

const GridContainer = (props: React.ComponentProps<typeof Box> & { innerRef?: React.Ref<unknown> }) => {
    const theme = useTheme();
    const { innerRef, ...boxProps } = props;
    return <Box sx={{ padding: theme.spacing(SPACING) }} ref={innerRef} {...boxProps}></Box>;
};

const skeletonButtonStyles = {
    small: {
        contained: {
            padding: "4px 10px",
        },
        outlined: {
            border: "1px",
            padding: "3px 9px",
        },
        text: {
            padding: "4px 5px",
        },
    },
    medium: {
        contained: {
            padding: "6px 16px",
        },
        outlined: {
            border: "1px",
            padding: "5px 15px",
        },
        text: {
            padding: "6px 8px",
        },
    },
    large: {
        contained: {
            padding: "8px 22px",
        },
        outlined: {
            border: "1px",
            padding: "7px 21px",
        },
        text: {
            padding: "8px 11px",
        },
    },
};

const skeletonButtonHeights = {
    small: 27.5,
    medium: 33,
    large: 42.25,
};

const SkeletonButton: React.FC<
    SkeletonProps<"button"> & {
        proportion?: "small" | "medium" | "large";
        buttonVariant?: "contained" | "outlined" | "text";
    }
> = (props) => {
    const { proportion = "medium", buttonVariant = "contained", ...skeletonProps } = props;
    return (
        <Skeleton
            variant="rounded"
            component="button"
            height={skeletonButtonHeights[proportion]}
            {...skeletonProps}
            sx={{
                alignItems: "center",
                appearance: "none",
                boxShadow: "none",
                border: "none",
                display: "inline-flex",
                boxSizing: "border-box",
                position: "relative",
                minWidth: "64px",
                outlineStyle: "none",
                outlineWidth: "0px",
                margin: 0,
                lineHeight: "1.75!important",
                width: "100%",
                fontSize: "0.696429rem",
                ...skeletonButtonStyles[proportion][buttonVariant],
                ...skeletonProps?.sx,
            }}
        />
    );
};

const spacerStyles = createStyles(() => {
    return { root: { height: "8px" } };
});

const Spacer = withStyles(spacerStyles)(Box);

const Title = (props: any) => {
    return <Typography sx={{ textTransform: "uppercase" }} {...props} />;
};

export { Button, Card, Grid, GridContainer, SkeletonButton, Spacer, Title };
